import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { useSliderHeight } from '../../hooks';
import { RegBanner, RegSlideProps } from './components/RegBanner';
import { RegistrationForm } from '@/components/dialogs/Registration/components/RegistrationForm';
import { NativeSlider } from '@/components/NativeSlider';
import {
  SliderArrow,
  SliderWithInsideArrows,
} from '@/components/NativeSlider/helpers';
import { useResizeObserver } from '@/hooks';
import useEstonia from '@/hooks/estonia';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore } from '@/store';

import { ScRegFormAbsoluteWrapper } from './components/RegBanner/styled';
import { ScSliderContainer } from './styled';
import { ScContainer } from '@/styles/common';

const MARGIN_GAP = 24;
const DEKSTOP_GAP = 145;
const MOBILE_GAP = 505;

export const RegistrationSlider = memo(() => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [elementHeight, setElementHeight] = useState(0);

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    setElementHeight(entry.contentRect.height);
  }, []);

  useResizeObserver(elementRef, handleResize);

  const { regSlides, isMobileDevice } = useServer();

  const height = useSliderHeight();
  const availableCrossSell = useCommonStore.use.availableCrossSell();
  const { isEstonia } = useEstonia();

  const normalizedRegSlides = useMemo((): RegSlideProps[] => {
    if (!regSlides) return [];

    return regSlides.map(
      (
        {
          charactersDesktop,
          backgroundDesktop,
          backgroundMobile,
          charactersMobile,
          title,
          titleColor,
          benefits,
        },
        index
      ) => {
        const advantages =
          benefits?.map(({ text, crossSellText, icon }) => ({
            text: availableCrossSell && crossSellText ? crossSellText : text,
            icon: icon?.url,
          })) || [];

        const gap = isMobileDevice ? MOBILE_GAP : DEKSTOP_GAP;
        const isTooHigh = height - MARGIN_GAP - elementHeight - gap;
        const newHeight = elementHeight + MARGIN_GAP + gap;
        return {
          backgroundDesktop: backgroundDesktop?.url,
          backgroundMobile: backgroundMobile?.url,
          characterImage: isMobileDevice
            ? charactersMobile?.url
            : charactersDesktop?.url,
          title,
          titleColor,
          advantages,
          height: isTooHigh > 0 ? height : newHeight,
          priority: index === 0,
          loading: index === 0 ? 'eager' : 'lazy',
        };
      }
    );
  }, [
    regSlides,
    isMobileDevice,
    elementHeight,
    isEstonia,
    availableCrossSell,
    height,
  ]);

  const arrows: SliderWithInsideArrows = useMemo(
    () => ({
      type: SliderArrow.INSIDE,
      insetLeftArrow: isMobileDevice ? '191px auto auto 32px' : undefined,
      insetRightArrow: isMobileDevice ? '191px 32px auto auto' : undefined,
      visibleForMobile: true,
    }),
    [isMobileDevice]
  );

  return (
    <ScSliderContainer>
      <NativeSlider<RegSlideProps>
        slide={RegBanner}
        slides={normalizedRegSlides}
        loop
        arrows={arrows}
        autoplay={{
          delay: 8000,
          pauseOnMouseEnter: true,
        }}
      />
      {!isEstonia && (
        <ScContainer>
          <ScRegFormAbsoluteWrapper ref={elementRef}>
            <RegistrationForm
              formId="one-step-reg"
              isOneStepRegistration
              openInnerModalsInPortal
              isSliderShortForm
            />
          </ScRegFormAbsoluteWrapper>
        </ScContainer>
      )}
    </ScSliderContainer>
  );
});

export default RegistrationSlider;
